import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery } from "@apollo/client";
import {
    ArrowRight,
    Calendar,
    CalendarCheck,
    CalendarClock,
    Clock,
    MapPin,
    Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const STREAM_TEAM_EVENTS_QUERY = gql`
    query StreamTeamEvents(
        $status: String
        $eventType: String
        $upcomingOnly: Boolean
        $pastOnly: Boolean
    ) {
        streamTeamEvents(
            status: $status
            eventType: $eventType
            upcomingOnly: $upcomingOnly
            pastOnly: $pastOnly
        ) {
            id
            createdBy {
                id
                player {
                    id
                    name
                }
            }
            title
            description
            summary
            eventType
            status
            locationType
            locationDetails
            startTime
            endTime
            joinUrl
            joinInstructions
            isPast
            isUpcoming
            isLiveNow
            createdAt
        }
    }
`;

interface StreamTeamEvent {
    id: string;
    createdBy: {
        id: string;
        player: {
            id: string;
            name: string;
        };
    };
    title: string;
    description: string;
    summary: string;
    eventType: string;
    status: string;
    locationType: string;
    locationDetails: string;
    startTime: string;
    endTime: string | null;
    joinUrl: string;
    joinInstructions: string;
    isPast: boolean;
    isUpcoming: boolean;
    isLiveNow: boolean;
    createdAt: string;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
    subathon: "Subathon",
    charity: "Charity Event",
    competition: "Competition",
    community: "Community Event",
    tournament: "Tournament",
    collaboration: "Collaboration",
    other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
    draft: "Draft",
    scheduled: "Scheduled",
    live: "Live",
    completed: "Completed",
    cancelled: "Cancelled",
};

function formatEventDate(startTime: string, endTime: string | null): string {
    const start = new Date(startTime);
    const startFormatted = start.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    const startTimeFormatted = start.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });

    if (endTime) {
        const end = new Date(endTime);
        const isSameDay = start.toDateString() === end.toDateString();
        if (isSameDay) {
            const endTimeFormatted = end.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            });
            return `${startFormatted} • ${startTimeFormatted} - ${endTimeFormatted}`;
        } else {
            const endFormatted = end.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
            });
            return `${startFormatted} at ${startTimeFormatted} → ${endFormatted}`;
        }
    }
    return `${startFormatted} at ${startTimeFormatted}`;
}

function getTimeUntilOrSince(
    startTime: string,
    endTime: string | null,
    isPast: boolean,
): string {
    const now = new Date();
    const start = new Date(startTime);

    if (isPast) {
        const end = endTime ? new Date(endTime) : start;
        const diffMs = now.getTime() - end.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks > 0) {
            return `Ended ${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
        }
        if (diffDays > 0) {
            return `Ended ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
        }
        return "Ended today";
    }

    const diffMs = start.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
        return `Starts in ${diffDays} day${diffDays > 1 ? "s" : ""}`;
    }
    if (diffHours > 0) {
        return `Starts in ${diffHours} hour${diffHours > 1 ? "s" : ""}`;
    }
    if (diffMinutes > 0) {
        return `Starts in ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
    }
    return "Starting now";
}

function EventCard({
    event,
    variant = "default",
}: {
    event: StreamTeamEvent;
    variant?: "default" | "compact";
}) {
    const navigate = useNavigate();

    return (
        <div
            className={`group border-brand_stream_team relative cursor-pointer rounded-xl border-2 bg-zinc-900 p-4 sm:p-6 ${
                variant === "compact" ? "p-3 sm:p-4" : ""
            }`}
            onClick={() => navigate(`/events/${event.id}`)}
        >
            {/* Status badge */}
            <div className="mb-3 flex items-center gap-2">
                {event.isLiveNow ? (
                    <span className="flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-white"></span>
                        LIVE NOW
                    </span>
                ) : event.isPast ? (
                    <span className="rounded-full border border-zinc-600 bg-zinc-700 px-3 py-1 text-xs font-medium text-zinc-300">
                        Past
                    </span>
                ) : (
                    <span className="rounded-full border border-green-600/50 bg-green-900/30 px-3 py-1 text-xs font-medium text-green-400">
                        {STATUS_LABELS[(event.status || "").toLowerCase()] ||
                            event.status}
                    </span>
                )}
                <span className="rounded-full border border-zinc-600 bg-zinc-900 px-3 py-1 text-xs font-medium text-zinc-400">
                    {EVENT_TYPE_LABELS[(event.eventType || "").toLowerCase()] ||
                        event.eventType}
                </span>
            </div>

            {/* Title */}
            <h3 className="font-prviking text-brand mb-2 text-xl sm:text-2xl">
                {convertLetters(event.title)}
            </h3>

            {/* Summary */}
            {event.summary && (
                <p className="font-garamond mb-4 line-clamp-2 text-sm text-zinc-300 sm:text-base">
                    {event.summary}
                </p>
            )}

            {/* Date/Time */}
            <div className="mb-3 flex items-center gap-2 text-sm text-zinc-400">
                <Calendar className="h-4 w-4" />
                <span className="font-garamond">
                    {formatEventDate(event.startTime, event.endTime)}
                </span>
            </div>

            {/* Time until/since */}
            <div className="mb-4 flex items-center gap-2 text-sm text-zinc-400">
                <Clock className="h-4 w-4" />
                <span className="font-garamond italic">
                    {getTimeUntilOrSince(
                        event.startTime,
                        event.endTime,
                        event.isPast,
                    )}
                </span>
            </div>

            {/* Location */}
            <div className="mb-4 flex items-center gap-2 text-sm text-zinc-400">
                <MapPin className="h-4 w-4" />
                <span className="font-garamond">
                    {(event.locationType || "").toLowerCase() === "online"
                        ? "Online Event"
                        : (event.locationType || "").toLowerCase() ===
                            "in_person"
                          ? "In Person"
                          : (event.locationType || "").toLowerCase() ===
                              "hybrid"
                            ? "Hybrid"
                            : "Online Event"}
                    {event.locationDetails && ` • ${event.locationDetails}`}
                </span>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
                <span className="font-garamond text-sm text-zinc-500">
                    By {event.createdBy.player.name}
                </span>
                <button className="bg-brand hover:bg-brand/90 flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-zinc-900 transition-colors">
                    Find out more <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default function StreamTeamEventsPage() {
    const [filter, setFilter] = useState<"all" | "upcoming" | "past">("all");
    const [eventTypeFilter, setEventTypeFilter] = useState<string>("");

    const { data, loading } = useQuery<{
        streamTeamEvents: StreamTeamEvent[];
    }>(STREAM_TEAM_EVENTS_QUERY, {
        variables: {
            status: filter === "all" ? null : undefined,
            eventType: eventTypeFilter || undefined,
            upcomingOnly: filter === "upcoming",
            pastOnly: filter === "past",
        },
    });

    const events = data?.streamTeamEvents || [];
    const upcomingEvents = events.filter((e) => !e.isPast && !e.isLiveNow);
    const liveEvents = events.filter((e) => e.isLiveNow);
    const pastEvents = events.filter((e) => e.isPast);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <div className="border-brand inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                    <p className="text-stats font-garamond mt-4 text-lg">
                        Loading events...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            {/* Header */}
            <div className="mb-8 text-center sm:mb-12">
                <h1 className="font-prviking text-brand mb-3 text-3xl sm:mb-4 sm:text-5xl lg:text-6xl">
                    {convertLetters("Events")}
                </h1>
                <p className="font-garamond mx-auto max-w-3xl text-base text-zinc-300 sm:text-lg lg:text-xl">
                    Join us for all official RAVENGUARD events — from
                    tournaments and charity streams to subathons, milestones,
                    and special broadcasts.
                </p>
            </div>

            {/* Stats */}
            <div className="mb-8 flex flex-wrap justify-center gap-6 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                    <CalendarCheck className="text-brand h-5 w-5" />
                    <span className="font-garamond">
                        {events.length} Total Events
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-green-500" />
                    <span className="font-garamond">
                        {upcomingEvents.length} Upcoming
                    </span>
                </div>
                {liveEvents.length > 0 && (
                    <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-red-500" />
                        <span className="font-garamond">
                            {liveEvents.length} Live Now
                        </span>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="mb-8 flex flex-wrap justify-center gap-3">
                {(["all", "upcoming", "past"] as const).map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`font-garamond rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === f
                                ? "bg-brand text-zinc-900"
                                : "border border-zinc-700 bg-zinc-800 text-zinc-300 hover:border-red-500"
                        }`}
                    >
                        {f === "all"
                            ? "All Events"
                            : f === "upcoming"
                              ? "Upcoming"
                              : "Past Events"}
                    </button>
                ))}
            </div>

            {/* Live Events Section */}
            {liveEvents.length > 0 && filter !== "past" && (
                <div className="mb-12">
                    <h2 className="font-prviking text-brand mb-6 flex items-center gap-3 text-2xl sm:text-3xl">
                        <span className="relative flex h-3 w-3">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                        </span>
                        {convertLetters("Live Now")}
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {liveEvents.map((event) => (
                            <EventCard key={event.id} event={event} />
                        ))}
                    </div>
                </div>
            )}

            {/* Upcoming Events Section */}
            {upcomingEvents.length > 0 && filter !== "past" && (
                <div className="mb-12">
                    <h2 className="font-prviking text-brand mb-6 text-2xl sm:text-3xl">
                        {convertLetters("Upcoming Events")}
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {upcomingEvents.map((event) => (
                            <EventCard key={event.id} event={event} />
                        ))}
                    </div>
                </div>
            )}

            {/* No Upcoming Events */}
            {upcomingEvents.length === 0 &&
                liveEvents.length === 0 &&
                filter !== "past" && (
                    <div className="border-brand_stream_team mb-12 rounded-xl border-2 bg-zinc-900 p-8 text-center">
                        <div className="text-brand mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-zinc-700/50">
                            <Clock className="h-8 w-8" />
                        </div>
                        <h3 className="font-prviking text-brand mb-2 text-xl">
                            {convertLetters("No Upcoming Events")}
                        </h3>
                        <p className="font-garamond text-zinc-400">
                            There are no upcoming events scheduled at the
                            moment. Check out our completed events below!
                        </p>
                    </div>
                )}

            {/* Past Events Section */}
            {pastEvents.length > 0 && filter !== "upcoming" && (
                <div className="mb-12">
                    <h2 className="font-prviking text-brand mb-6 text-2xl sm:text-3xl">
                        {convertLetters("Completed Events")}{" "}
                        <span className="font-garamond text-lg text-zinc-400">
                            {pastEvents.length} Events
                        </span>
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {pastEvents.map((event) => (
                            <EventCard key={event.id} event={event} />
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {events.length === 0 && (
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-12 text-center">
                    <Calendar className="text-brand mx-auto mb-4 h-16 w-16 opacity-50" />
                    <h3 className="font-prviking text-brand mb-2 text-2xl">
                        {convertLetters("No Events Found")}
                    </h3>
                    <p className="font-garamond text-zinc-400">
                        There are no events matching your criteria.
                    </p>
                </div>
            )}
        </div>
    );
}
